import React, { useState, useEffect } from 'react';

interface TestingWorkflowStep {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  completedDate?: string;
  notes?: string;
  estimatedDuration: string;
  dependencies?: string[];
}

interface TestingRequest {
  id: string;
  vendorId: string;
  vendorName: string;
  testerId: string;
  testerName: string;
  productInfo: {
    id: string;
    name: string;
    type: string;
    strain: string;
    sampleSize: string;
    specialInstructions?: string;
  };
  status: 'pending_acceptance' | 'accepted' | 'sample_prepared' | 'shipped' | 'delivered' | 'testing' | 'review_submitted' | 'completed' | 'cancelled';
  timeline: {
    requestDate: string;
    acceptanceDate?: string;
    shipDate?: string;
    deliveryDate?: string;
    reviewSubmissionDate?: string;
    completionDate?: string;
  };
  shipping: {
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
    };
    trackingNumber?: string;
    carrier?: string;
    estimatedDelivery?: string;
  };
  communication: TestingMessage[];
  workflow: TestingWorkflowStep[];
  compensation: {
    baseAmount: number;
    qualityBonus?: number;
    speedBonus?: number;
    totalAmount: number;
  };
}

interface TestingMessage {
  id: string;
  fromId: string;
  fromName: string;
  fromType: 'vendor' | 'tester' | 'staff';
  message: string;
  timestamp: string;
  attachments?: string[];
}

interface TestingWorkflowProps {
  requestId: string;
  userType: 'vendor' | 'tester' | 'staff';
  userId: string;
}

export default function TestingWorkflow({ requestId, userType, userId }: TestingWorkflowProps) {
  const [request, setRequest] = useState<TestingRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [showCommunication, setShowCommunication] = useState(false);

  useEffect(() => {
    const fetchRequest = async () => {
      setLoading(true);
      
      setTimeout(() => {
        const mockWorkflow: TestingWorkflowStep[] = [
          {
            id: '1',
            name: 'Request Sent',
            status: 'completed',
            completedDate: '2024-01-15',
            estimatedDuration: '0 days',
            notes: 'Testing request sent to tester'
          },
          {
            id: '2',
            name: 'Tester Acceptance',
            status: 'completed',
            completedDate: '2024-01-16',
            estimatedDuration: '1-2 days',
            dependencies: ['1'],
            notes: 'Tester accepted request within 18 hours'
          },
          {
            id: '3',
            name: 'Sample Preparation',
            status: 'completed',
            completedDate: '2024-01-17',
            estimatedDuration: '1-2 days',
            dependencies: ['2'],
            notes: 'Sample packaged with tracking information'
          },
          {
            id: '4',
            name: 'Sample Shipped',
            status: 'completed',
            completedDate: '2024-01-18',
            estimatedDuration: '0 days',
            dependencies: ['3'],
            notes: 'Shipped via USPS Priority Mail'
          },
          {
            id: '5',
            name: 'Sample Delivered',
            status: 'completed',
            completedDate: '2024-01-20',
            estimatedDuration: '2-3 days',
            dependencies: ['4'],
            notes: 'Delivery confirmed by tester'
          },
          {
            id: '6',
            name: 'Testing Period',
            status: 'in_progress',
            estimatedDuration: '3-7 days',
            dependencies: ['5'],
            notes: 'Tester evaluating product effects and quality'
          },
          {
            id: '7',
            name: 'Review Submission',
            status: 'pending',
            estimatedDuration: '1 day',
            dependencies: ['6']
          },
          {
            id: '8',
            name: 'Review Verification',
            status: 'pending',
            estimatedDuration: '1-2 days',
            dependencies: ['7']
          },
          {
            id: '9',
            name: 'Payment Processing',
            status: 'pending',
            estimatedDuration: '1 day',
            dependencies: ['8']
          }
        ];

        const mockMessages: TestingMessage[] = [
          {
            id: '1',
            fromId: 'vendor-001',
            fromName: 'Premium Kratom Co',
            fromType: 'vendor',
            message: 'Hello! We\'d love to have you test our new Red Maeng Da batch. This is a premium strain with high alkaloid content.',
            timestamp: '2024-01-15T10:00:00Z'
          },
          {
            id: '2',
            fromId: 'tester-001',
            fromName: 'Sarah M.',
            fromType: 'tester',
            message: 'I\'d be happy to test this for you! Red Maeng Da is one of my specialties. When can you ship the sample?',
            timestamp: '2024-01-16T09:30:00Z'
          },
          {
            id: '3',
            fromId: 'vendor-001',
            fromName: 'Premium Kratom Co',
            fromType: 'vendor',
            message: 'Great! I\'ll prepare a 10g sample and ship it today. You should receive tracking information shortly.',
            timestamp: '2024-01-16T11:15:00Z'
          },
          {
            id: '4',
            fromId: 'tester-001',
            fromName: 'Sarah M.',
            fromType: 'tester',
            message: 'Sample received! The packaging looks professional and the powder has a nice deep red color. I\'ll start testing tonight.',
            timestamp: '2024-01-20T16:45:00Z'
          }
        ];

        const mockRequest: TestingRequest = {
          id: requestId,
          vendorId: 'vendor-001',
          vendorName: 'Premium Kratom Co',
          testerId: 'tester-001',
          testerName: 'Sarah M.',
          productInfo: {
            id: 'prod-001',
            name: 'Premium Red Maeng Da',
            type: 'Powder',
            strain: 'Red Maeng Da',
            sampleSize: '10g',
            specialInstructions: 'Please test for both evening relaxation and pain relief effects'
          },
          status: 'testing',
          timeline: {
            requestDate: '2024-01-15',
            acceptanceDate: '2024-01-16',
            shipDate: '2024-01-18',
            deliveryDate: '2024-01-20'
          },
          shipping: {
            address: {
              street: '123 Main St',
              city: 'San Diego',
              state: 'CA',
              zipCode: '92101'
            },
            trackingNumber: 'TRK123456789',
            carrier: 'USPS',
            estimatedDelivery: '2024-01-20'
          },
          communication: mockMessages,
          workflow: mockWorkflow,
          compensation: {
            baseAmount: 25,
            qualityBonus: 15,
            speedBonus: 10,
            totalAmount: 50
          }
        };

        setRequest(mockRequest);
        setLoading(false);
      }, 1000);
    };

    fetchRequest();
  }, [requestId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'in_progress': return '#3b82f6';
      case 'pending': return '#f59e0b';
      case 'failed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'in_progress': return '🔄';
      case 'pending': return '⏳';
      case 'failed': return '❌';
      default: return '⭕';
    }
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() || !request) return;

    const message: TestingMessage = {
      id: Date.now().toString(),
      fromId: userId,
      fromName: userType === 'vendor' ? 'Vendor User' : userType === 'tester' ? 'Tester User' : 'Staff User',
      fromType: userType,
      message: newMessage,
      timestamp: new Date().toISOString()
    };

    setRequest(prev => prev ? {
      ...prev,
      communication: [...prev.communication, message]
    } : null);

    setNewMessage('');
  };

  const handleStatusUpdate = (stepId: string, newStatus: string) => {
    if (!request) return;

    setRequest(prev => prev ? {
      ...prev,
      workflow: prev.workflow.map(step => 
        step.id === stepId 
          ? { ...step, status: newStatus as any, completedDate: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : undefined }
          : step
      )
    } : null);
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Loading testing workflow...</p>
      </div>
    );
  }

  if (!request) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Testing request not found</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Request Header */}
      <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', margin: '0 0 0.5rem 0', color: '#1f2937' }}>
              Testing Request #{request.id}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <span style={{ color: '#6b7280', fontSize: '1rem' }}>
                {request.productInfo.name}
              </span>
              <span style={{ 
                padding: '0.25rem 0.75rem', 
                borderRadius: '0.25rem', 
                fontSize: '0.75rem', 
                fontWeight: '600',
                backgroundColor: getStatusColor(request.status) + '20',
                color: getStatusColor(request.status),
                textTransform: 'capitalize'
              }}>
                {request.status.replace('_', ' ')}
              </span>
            </div>
            <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Vendor: {request.vendorName} | Tester: {request.testerName}
            </div>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
              ${request.compensation.totalAmount}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Total Compensation
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          padding: '1rem',
          backgroundColor: '#f9fafb',
          borderRadius: '0.375rem'
        }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Product</div>
            <div style={{ fontSize: '0.875rem', color: '#374151' }}>{request.productInfo.name}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Type</div>
            <div style={{ fontSize: '0.875rem', color: '#374151' }}>{request.productInfo.type}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Sample Size</div>
            <div style={{ fontSize: '0.875rem', color: '#374151' }}>{request.productInfo.sampleSize}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Tracking</div>
            <div style={{ fontSize: '0.875rem', color: '#374151' }}>
              {request.shipping.trackingNumber || 'Not shipped yet'}
            </div>
          </div>
        </div>

        {request.productInfo.specialInstructions && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '0.375rem', border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>
              Special Instructions:
            </div>
            <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
              {request.productInfo.specialInstructions}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
        {/* Workflow Timeline */}
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1f2937' }}>
            Testing Workflow
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {request.workflow.map((step, index) => (
              <div key={step.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                {/* Timeline connector */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '24px' }}>
                  <div style={{ 
                    width: '24px', 
                    height: '24px', 
                    borderRadius: '50%', 
                    backgroundColor: getStatusColor(step.status),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px'
                  }}>
                    {getStatusIcon(step.status)}
                  </div>
                  {index < request.workflow.length - 1 && (
                    <div style={{ 
                      width: '2px', 
                      height: '40px', 
                      backgroundColor: '#e5e7eb',
                      marginTop: '4px'
                    }}></div>
                  )}
                </div>
                
                {/* Step content */}
                <div style={{ flex: '1', paddingBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0' }}>
                      {step.name}
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {step.estimatedDuration}
                      </span>
                      {userType === 'staff' && step.status === 'in_progress' && (
                        <button
                          onClick={() => handleStatusUpdate(step.id, 'completed')}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Mark Complete
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {step.completedDate && (
                    <div style={{ fontSize: '0.75rem', color: '#10b981', marginBottom: '0.25rem' }}>
                      Completed: {new Date(step.completedDate).toLocaleDateString()}
                    </div>
                  )}
                  
                  {step.notes && (
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {step.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Communication Panel */}
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', margin: '0' }}>
              Communication
            </h3>
            <button
              onClick={() => setShowCommunication(!showCommunication)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              {showCommunication ? 'Hide' : 'Show'} Chat
            </button>
          </div>

          {showCommunication && (
            <div>
              {/* Message History */}
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                marginBottom: '1rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                padding: '1rem'
              }}>
                {request.communication.map(message => (
                  <div key={message.id} style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                        {message.fromName}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {new Date(message.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      padding: '0.75rem',
                      backgroundColor: message.fromType === userType ? '#eff6ff' : '#f9fafb',
                      borderRadius: '0.375rem'
                    }}>
                      {message.message}
                    </div>
                  </div>
                ))}
              </div>

              {/* New Message */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  style={{
                    flex: '1',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    resize: 'none',
                    height: '80px'
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: newMessage.trim() ? '#3b82f6' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                    height: '80px'
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          )}

          {/* Compensation Breakdown */}
          <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '0.375rem' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#166534', marginBottom: '0.75rem' }}>
              Compensation Breakdown
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#166534' }}>Base Amount:</span>
                <span style={{ color: '#166534', fontWeight: '500' }}>${request.compensation.baseAmount}</span>
              </div>
              {request.compensation.qualityBonus && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#166534' }}>Quality Bonus:</span>
                  <span style={{ color: '#166534', fontWeight: '500' }}>${request.compensation.qualityBonus}</span>
                </div>
              )}
              {request.compensation.speedBonus && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#166534' }}>Speed Bonus:</span>
                  <span style={{ color: '#166534', fontWeight: '500' }}>${request.compensation.speedBonus}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '1px solid #bbf7d0' }}>
                <span style={{ color: '#166534', fontWeight: '600' }}>Total:</span>
                <span style={{ color: '#166534', fontWeight: '600' }}>${request.compensation.totalAmount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}